import { useEffect, useState } from "react"
import { Box, CircularProgress, Autocomplete, TextField, Avatar, Typography } from "@mui/material"
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import { getDoctorListWithClinicMerge } from "@/services/doctorService"

function sleep(delay = 0) {
  return new Promise(resolve => {
    setTimeout(resolve, delay)
  })
}

const AutocompleteSearchField = ({ getValue, initialValue, approvedDoctor = null }) => {
  const [value, setValue] = useState(initialValue)
  const [inputValue, setInputValue] = useState("")
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState([])
  const [isLoading, setIsLoading] = useState(open && options.length === 0)

  useEffect(() => {
    if (inputValue === "") {
      setOptions([])
      setIsLoading(false)
      return
    }

    if (inputValue === value?.name) {
      return
    }

    setIsLoading(true)
    let active = true

    ;(async () => {
      await sleep(1e3)

      if (active) {
        try {
          const { data } = await getDoctorListWithClinicMerge({
            query: inputValue,
            approved: approvedDoctor,
          })

          if (data.length > 0) {
            data.map(item => {
              return {
                id: item.place_id,
                name: item.name,
                formattedAddress: item.googlePlace ? item.googlePlace.formattedAddress : "",
              }
            })
            setOptions(data)
          } else {
            setOptions([])
          }
        } catch (e) {
          console.error(e)
        }
        setIsLoading(false)
      }
    })()

    return () => {
      active = false
    }
  }, [inputValue]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (inputValue === value?.name) return
    if (!open) {
      setOptions([])
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getValue(value)
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Autocomplete
      id="asynchronous-google-place-search"
      value={value}
      onChange={(event, newValue) => {
        if (newValue) {
          setValue(newValue)
        } else {
          setValue(null)
        }
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue)
      }}
      sx={{ width: "100%", backgroundColor: "SHADES_000", borderRadius: 1 }}
      open={open}
      onOpen={() => {
        setOpen(true)
      }}
      onClose={() => {
        setOpen(false)
      }}
      onKeyDown={event => {
        if (event.key === "Enter") {
          // Prevent's default 'Enter' behavior.
          // event.defaultMuiPrevented = true
          // clear option, trigger search function
          setOptions([])
        }
      }}
      selectOnFocus={false}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={option => option.name}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Avatar
              sx={{ width: { xs: 48 }, height: { xs: 48 } }}
              alt="doctor-avatar"
              src={option.avatarImage}
            />
            <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography component="div" variant="headingMediumMedium">
                  {option.subjects && option.subjects.map(subject => subject.name).join(" ")}
                  {option.name} {option.title}
                </Typography>
                {option.approvedAt && <CheckCircleRoundedIcon color={"primary"} />}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {option.clinics.length > 0 &&
                  option.clinics.map(clinic => {
                    return (
                      <Box key={clinic.id}>
                        <Typography component="div" sx={{ color: "SHADES_500" }}>
                          {clinic.name || clinic.googlePlace?.name}
                        </Typography>
                        {clinic.googlePlace && (
                          <>
                            <Typography component="div" sx={{ color: "SHADES_500" }}>
                              {clinic.googlePlace.formattedAddress}
                            </Typography>
                          </>
                        )}
                      </Box>
                    )
                  })}
              </Box>
            </Box>
          </Box>
        </li>
      )}
      options={options}
      loading={isLoading}
      loadingText={inputValue ? `搜尋中...「${inputValue}」` : `請輸入醫事人員名稱`}
      noOptionsText={
        inputValue
          ? `找不到「${inputValue}」。${
              approvedDoctor
                ? "PinMed上沒有這位醫事人員，或此醫事人員已被認領。"
                : "PinMed上沒有這位醫事人員。"
            }`
          : `請輸入醫事人員名稱`
      }
      renderInput={params => (
        <TextField
          placeholder="請輸入醫事人員名稱"
          {...params}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

export default AutocompleteSearchField
